import React, { useState, useEffect } from "react";
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { useRegisterProgress } from "../../hooks/useRegisterProgress";
import styles from "./PutInOrderWord.module.css";
import PlayButton from "../../components/PlayButton/PlayButton";
import {Button} from "../../components/Button/Button";
import {Template} from "../../components/Template/Template";
import {shuffleArray} from "../../lib/utils";
import useAudioPlayback from "../../hooks/useAudioPlayback";

const SortableItem = SortableElement(({ value }) => (
    <div
        className={styles.sortableItem}
        style={{
            backgroundColor: value.right === undefined ? 'white' : value.right ? '#69f0ae' : '#ff5252',
            margin: '0px 10px',
            fontSize: 22,
        }}
    >
        {value.name}
    </div>
));

const SortableList = SortableContainer(({ items }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {items.map((value, index) => (
            <SortableItem axis="xy" key={`item-${value.order}`} index={index} value={value} />
        ))}
    </div>
));

const PutInOrderWord = ({ blockData, contentIndex, audioUrl, lessonId, step, totalIndexes }) => {
    const [items, setItems] = useState([]);
    const [checked, setChecked] = useState(false);
    const [solved, setSolved] = useState(false);

    const { state, togglePlayback, resetAudio} = useAudioPlayback(audioUrl, "listen_and_drag_to_put_in_order");

    // Hook to register progress.
    const { registerProgress } = useRegisterProgress(
        blockData.block_id,
        lessonId,
        blockData.sectionId,
        blockData.section,
        blockData.num,
        totalIndexes,
        "put_in_order_words_sentences"
    );

    useEffect(() => {
        initializeItems();
    }, [blockData.num]);

    // Initialize items by shuffling them
    const initializeItems = () => {
        const shuffledItems = blockData.complement.palavras.map((word, index) => ({
            name: word,
            order: index
        }));

        setItems(shuffleArray(shuffledItems));
    };

    const checkWordsSequence = () => {
        const updatedItems = items.map((item, index) => ({
            ...item,
            right: item.order === index
        }));
        setItems(updatedItems);

        return updatedItems;
    }

    // Check the order and update the accuracy percentage

    const calculateAccuracyPercentage = (correctCount, totalWords) => {
        return totalWords ? roundToTwoDecimals((correctCount / totalWords) * 100) : 0;
    };

    const roundToTwoDecimals = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

    const solve = () => {
        const sortedItems = [...items]
            .sort((a, b) => a.order - b.order)
            .map(item => ({ ...item, right: true }));

        setItems(sortedItems);
        setSolved(true);

        resetAudio();
        togglePlayback();
    };

    const resetState = () => {
        initializeItems();
        setChecked(false);
        setSolved(false);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        document.body.style.cursor = "";
        setItems(arrayMove(items, oldIndex, newIndex));
    };

    const onCheckClick = async () => {
        // Check if the order is correct
        const updatedItems = checkWordsSequence(false);

        const correctCount = updatedItems.filter(item => item.right).length;
        const totalWords = updatedItems.length;
        const accuracyPercentage = calculateAccuracyPercentage(correctCount, totalWords);
        const correct = correctCount === totalWords;

        if(correct) {
            // All items are correct, so we can solve the exercise
            setChecked(true);
            setSolved(true);

            togglePlayback();

        } else {
            setChecked(true);
            setSolved(false);
        }


        // Register accuracy based on correct items
        await registerProgress(true, accuracyPercentage, 1);
    };

    const onSolveClick = () => {
        solve();
    }

    const onTryAgainClick = () => {
        resetState();
    }

    const onContinueClick = () => {
        resetState();
        step('forward', true);
    }

    return (
        <Template showInstruction={blockData.content[contentIndex].type === 'instruction'}
                  instructionText={blockData.content[contentIndex].instruction} audioUrl={audioUrl} >

            <div className={styles.contentContainer}>

                <div className={styles.audioContainer}>
                    <PlayButton isPlaying={state === 'playing'} onClick={() => {togglePlayback()}} />
                </div>

                <div className={styles.wordsContainer}>
                    <SortableList
                        axis="xy"
                        onSortStart={() => (document.body.style.cursor = "grabbing")}
                        items={items}
                        onSortEnd={onSortEnd}
                    />
                </div>

                <div className={styles.buttonContainer}>

                    <Button className={styles.tryAgainButton} enabled={checked} onClick={onTryAgainClick} >
                        Try again
                    </Button>

                    {solved ? (
                        <Button className={styles.checkButton} onClick={onContinueClick}>
                            Continue
                        </Button>
                    ) : checked ? (
                        <Button className={styles.checkButton} onClick={onSolveClick}>
                            Solve
                        </Button>
                    ) :(
                        <Button className={styles.checkButton} onClick={onCheckClick}>
                            Check
                        </Button>
                    )}


                </div>

            </div>

        </Template>
    );
};

export default PutInOrderWord;